import { useEffect } from 'react';
import { clsx } from 'clsx';
import {
  Loader2,
  Play,
  AlertCircle,
  Layers,
  Zap,
} from 'lucide-react';
import { useResolutionPipelineStore } from '@/stores/resolutionPipeline';
import { useKnowledgeStore } from '@/stores/knowledge';
import type { ClusterSummaryData } from '@/services/api';

function formatLabel(s: string): string {
  return s
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  resolved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  skipped: 'bg-slate-100 text-slate-600 border-slate-200',
  failed: 'bg-rose-50 text-rose-700 border-rose-200',
};

export function ClusterExplorer() {
  const {
    clusterFilters,
    clusters,
    selectedCluster,
    clusterLoading,
    clusterError,
    resolvingEntityType,
    resolveStatus,
    setClusterFilters,
    runClusterFind,
    selectCluster,
    runResolutionForCurrentFilters,
  } = useResolutionPipelineStore();

  const { entityTypes, fetchEntityTypes } = useKnowledgeStore();

  useEffect(() => {
    fetchEntityTypes();
  }, [fetchEntityTypes]);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="min-w-[10rem]">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
              Entity Type
            </label>
            <select
              value={clusterFilters.entity_type}
              onChange={(e) =>
                setClusterFilters({ entity_type: e.target.value })
              }
              className="w-full text-xs bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-teal"
            >
              {entityTypes.length === 0 && (
                <option value="market_concept">market_concept</option>
              )}
              {entityTypes.map((t) => (
                <option key={t} value={t}>
                  {formatLabel(t)}
                </option>
              ))}
            </select>
          </div>
          <div className="min-w-[10rem]">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
              Client UID (optional)
            </label>
            <input
              type="text"
              value={clusterFilters.client_id}
              onChange={(e) => setClusterFilters({ client_id: e.target.value })}
              className="w-full text-xs bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-teal"
            />
          </div>
          <div className="min-w-[14rem] flex-1">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
              Similarity threshold
              <span className="ml-1 text-navy-900 font-bold">
                {clusterFilters.similarity_threshold.toFixed(2)}
              </span>
            </label>
            <input
              type="range"
              min={0.5}
              max={1}
              step={0.01}
              value={clusterFilters.similarity_threshold}
              onChange={(e) =>
                setClusterFilters({
                  similarity_threshold: Number(e.target.value),
                })
              }
              className="w-full accent-brand-teal h-1.5"
            />
          </div>
          <button
            onClick={runClusterFind}
            disabled={clusterLoading}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold bg-brand-teal text-navy-900 rounded-lg hover:bg-brand-teal/90 disabled:opacity-40"
          >
            {clusterLoading ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Play size={12} />
            )}
            Find Clusters
          </button>
          <button
            onClick={runResolutionForCurrentFilters}
            disabled={resolvingEntityType}
            title="Run the full LLM resolution pipeline for this entity type + client"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold bg-white border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 disabled:opacity-40"
          >
            {resolvingEntityType ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Zap size={12} />
            )}
            Run LLM Resolution
          </button>
        </div>
        {clusterError && (
          <div className="flex items-center gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
            <AlertCircle size={12} />
            {clusterError}
          </div>
        )}
        {resolveStatus && (
          <div className="text-xs bg-brand-teal/5 border border-brand-teal/10 rounded-lg px-3 py-2 text-brand-teal-dark">
            {resolveStatus}
          </div>
        )}
      </div>

      {clusterLoading && clusters.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-sm text-slate-400 text-center">
          <Loader2 size={14} className="animate-spin inline mr-2" />
          Finding clusters...
        </div>
      ) : clusters.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <Layers size={24} className="text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">
            Pick an entity type + threshold, then click <strong>Find Clusters</strong>.
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Clusters are session-only in Phase 3 (Phase 4 persists them).
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-5 bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-2 border-b border-slate-100 bg-slate-50/60 flex items-center justify-between">
              <span className="text-xs font-semibold text-navy-900">
                Clusters
                <span className="ml-1 text-slate-400 font-mono">({clusters.length})</span>
              </span>
            </div>
            <div className="max-h-[60vh] overflow-auto divide-y divide-slate-100">
              {clusters.map((c) => (
                <ClusterRow
                  key={c.cluster_id}
                  cluster={c}
                  selected={selectedCluster?.cluster_id === c.cluster_id}
                  onClick={() => selectCluster(c)}
                />
              ))}
            </div>
          </div>
          <div className="col-span-7 bg-white rounded-xl border border-slate-200 overflow-hidden">
            <ClusterDetail cluster={selectedCluster} />
          </div>
        </div>
      )}
    </div>
  );
}

function ClusterRow({
  cluster,
  selected,
  onClick,
}: {
  cluster: ClusterSummaryData;
  selected: boolean;
  onClick: () => void;
}) {
  const statusClass =
    STATUS_COLORS[cluster.status] ?? 'bg-slate-50 text-slate-500 border-slate-200';
  const range =
    cluster.max_similarity > cluster.min_similarity
      ? `${(cluster.min_similarity * 100).toFixed(0)}–${(
          cluster.max_similarity * 100
        ).toFixed(0)}%`
      : `${(cluster.avg_similarity * 100).toFixed(0)}%`;

  return (
    <button
      onClick={onClick}
      className={clsx(
        'w-full text-left px-4 py-3 transition-colors',
        selected ? 'bg-brand-teal/5 border-l-4 border-l-brand-teal' : 'hover:bg-slate-50 border-l-4 border-l-transparent',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-slate-800 truncate">
          {cluster.entity_names[0] ?? cluster.cluster_key}
        </span>
        <span
          className={clsx(
            'text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border flex-shrink-0',
            statusClass,
          )}
        >
          {cluster.status}
        </span>
      </div>
      <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-500">
        <span>{cluster.entity_count} members</span>
        <span>sim {range}</span>
        <span className="ml-auto font-mono text-[10px] text-slate-400 truncate max-w-[7rem]">
          {cluster.cluster_key}
        </span>
      </div>
    </button>
  );
}

function ClusterDetail({ cluster }: { cluster: ClusterSummaryData | null }) {
  if (!cluster) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-slate-400 p-8">
        Select a cluster to see members
      </div>
    );
  }
  return (
    <div className="flex flex-col h-full">
      <div className="px-5 py-4 border-b border-slate-200">
        <h3 className="text-sm font-semibold text-navy-900">
          {cluster.entity_names[0] ?? cluster.cluster_key}
        </h3>
        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
          <code>{cluster.cluster_id}</code>
          <span>{cluster.entity_count} members</span>
          <span>
            avg sim {(cluster.avg_similarity * 100).toFixed(0)}% · min{' '}
            {(cluster.min_similarity * 100).toFixed(0)}% · max{' '}
            {(cluster.max_similarity * 100).toFixed(0)}%
          </span>
        </div>
      </div>
      <div className="flex-1 overflow-auto px-5 py-4">
        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
          Member concepts ({cluster.entity_names.length})
        </div>
        <ol className="space-y-1 list-decimal list-inside text-sm text-slate-700">
          {cluster.entity_names.map((n, i) => (
            <li key={`${cluster.cluster_id}-${i}`} className="truncate">
              {n}
            </li>
          ))}
        </ol>
        <p className="text-xs text-slate-500 italic mt-4">
          Pairwise similarity scores aren't included in the cluster summary
          payload — run <strong>Run LLM Resolution</strong> to process this
          cluster; the resulting ResolutionResult records the full reasoning
          for each merge/link/distinct decision.
        </p>
      </div>
    </div>
  );
}
